import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";
import { getVideos, videosAccess, recordVideoView } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fieldClass =
  "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/40";

function LockedCard({ ch }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={`video-locked-${ch.id}`}>
      <div className="relative">
        <img src={ch.thumbnail} alt={ch.title} className="w-full aspect-video object-cover" />
        <div className="absolute inset-0 bg-stone-900/45 grid place-items-center">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-white/90 backdrop-blur">
            <i className="fa-solid fa-lock text-[#1F3D2E]" />
          </div>
        </div>
      </div>
      <div className="p-5">
        <p className="overline-label">Chapter {ch.number}</p>
        <h3 className="font-playfair text-lg text-stone-900 mt-1">{ch.title}</h3>
        <p className="text-sm text-stone-500 mt-1 leading-relaxed">{ch.description}</p>
      </div>
    </div>
  );
}

function UnlockedCard({ ch }) {
  const viewed = useRef(false);
  const onPlay = () => {
    if (viewed.current) return;
    viewed.current = true;
    recordVideoView(ch.id).catch(() => {});
  };
  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={`video-unlocked-${ch.id}`}>
      <video
        controls
        preload="metadata"
        poster={ch.thumbnail}
        onPlay={onPlay}
        className="w-full aspect-video bg-black"
        data-testid={`video-player-${ch.id}`}
      >
        <source src={ch.video_url} type="video/mp4" />
        Your browser doesn't support embedded video.
      </video>
      <div className="p-5">
        <p className="overline-label">Chapter {ch.number}</p>
        <h3 className="font-playfair text-lg text-stone-900 mt-1">{ch.title}</h3>
        <p className="text-sm text-stone-500 mt-1 leading-relaxed">{ch.description}</p>
        <a
          href={`${BACKEND}${ch.download_url}`}
          data-testid={`video-download-${ch.id}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-4 hover:gap-3 transition-all"
        >
          <i className="fa-solid fa-file-arrow-down" /> Download this chapter
        </a>
      </div>
    </div>
  );
}

export default function VideoSeries() {
  const [chapters, setChapters] = useState([]);
  const [unlocked, setUnlocked] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getVideos()
      .then((d) => setChapters(d.chapters || []))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your first name.");
    if (!email.trim()) return toast.error("Please enter your email.");
    if (!consent) return toast.error("Please check the consent box to continue.");
    setLoading(true);
    try {
      const res = await videosAccess({ name, email, consent });
      setChapters(res.chapters || []);
      setUnlocked(true);
      toast.success(res?.message || "Your videos are unlocked.");
      setTimeout(
        () => document.getElementById("chapter-videos")?.scrollIntoView({ behavior: "smooth" }),
        80
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-8" data-testid="video-series-page">
      {/* Hero */}
      <Section className="pt-14 sm:pt-20">
        <div className="max-w-3xl">
          <Eyebrow>Companion video series</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.08] mt-5">
            Watch the Sacred Flame, chapter by chapter
          </h1>
          <p className="text-lg text-stone-600 leading-relaxed mt-6">
            A short video accompanies each chapter of the guidebook — Jonathan walking you through the
            ideas in his own words. Watch online or download them to keep and revisit privately, any
            time you like.
          </p>
        </div>
      </Section>

      {/* Email gate */}
      {!unlocked && (
        <Section className="mt-12">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="grid sm:grid-cols-2 gap-5">
              {chapters.map((ch) => (
                <LockedCard key={ch.id} ch={ch} />
              ))}
            </div>
            <div className="bg-white rounded-[2rem] border border-stone-200 p-8 shadow-sm">
              <Eyebrow>Free access</Eyebrow>
              <h2 className="font-playfair text-2xl text-stone-900 mt-2">
                Enter your email to watch or download
              </h2>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                We'll unlock the videos here and send you a link so you can come back to them anytime.
                No phone number, no pressure.
              </p>
              <form onSubmit={submit} className="space-y-4 mt-6" data-testid="video-access-form">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">First name</label>
                  <input data-testid="video-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your first name" className={fieldClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Email address</label>
                  <input data-testid="video-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={fieldClass} />
                </div>
                <label className="flex items-start gap-3 text-sm text-stone-600">
                  <input type="checkbox" data-testid="video-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-[#1F3D2E]" />
                  <span>I'd like access to the video series and understand my email may be used for occasional, related follow-up.</span>
                </label>
                <button type="submit" disabled={loading} data-testid="video-submit" className="btn-terracotta w-full rounded-full px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
                  {loading ? "Unlocking\u2026" : "Watch the videos"}
                </button>
              </form>
            </div>
          </div>
        </Section>
      )}

      {/* Unlocked videos */}
      {unlocked && (
        <Section className="mt-12" id="chapter-videos">
          <div className="flex items-center gap-2 text-[#4A7C59] mb-6">
            <i className="fa-solid fa-lock-open" />
            <p className="text-sm font-semibold">Access unlocked — watch or download below.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {chapters.map((ch) => (
              <UnlockedCard key={ch.id} ch={ch} />
            ))}
          </div>
          <div className="mt-10 rounded-2xl bg-[#EEF2ED] border border-[#1F3D2E]/15 p-6">
            <p className="text-sm text-stone-700">When you're ready, take the next private step:</p>
            <Link to="/assessments/sacred-flame-readiness" data-testid="video-assessment-cta" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-2 hover:gap-3 transition-all">
              Take the Sacred Flame Readiness Assessment <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </Section>
      )}

      <Section className="mt-16">
        <DisclaimerNote
          className="max-w-3xl"
          text="These videos are provided for educational and personal-development purposes and are not intended to diagnose, treat, cure, or prevent any medical or mental-health condition. Individual experiences vary."
        />
      </Section>
    </div>
  );
}
